const ItemType = require('@/lib/Items/ItemType');
const OrderItemGroup = require('@/lib/Orders/OrderItemGroup');
const { cloneShallow } = require('@/functions/clone');
const {
    compareItemTypesByTabOrder,
    getItemTypeTabIcon,
    getItemTypeTabLangKey,
} = require('@/lib/Items/itemTypeTabs');

/**
 * Get the item type to use for grouping/tab purposes for an order line.
 *
 * This is the same itemType as the line except for food products which are products where products.isFood === true.
 *
 * This method splits out food products so this is the same as the Sell screen where food is in its own tab thanks to
 * SaleableItemService.loadProducts().
 * This means the tabs on both screens are in sync as far as they can be (not all item types are sellable in ProPoint)
 *
 * NOTE: this does not update orderLine.itemType.
 *
 * @param {OrderLine} orderLine
 *
 * @return {string}
 */
const getGroupingItemType = (orderLine) => {
    if (orderLine.getItemType() === ItemType.PRODUCT && orderLine.getItem()?.isFood) {
        return ItemType.PRODUCT_FOOD;
    }

    return orderLine.getItemType();
};

/**
 * Create OrderItemGroups from an array of OrderLines.
 * The lines will be split up by:
 * - Item type (including split out food products)
 * - Session (for session-based tickets)
 * - Booking Group (where applicable)
 *
 * @param {OrderLine[]} items
 *
 * @return {OrderItemGroup[]}
 */
const createItemGroups = (items) => {
    /**
     * @type {Object<string, OrderItemGroup>}
     */
    const itemGroups = {};

    /**
     * As we iterate through the items, we build a map of all items by their ID.
     *
     * @type {Object<number, OrderLine>}
     */
    const allLinesByID = {};

    for (let i = 0; i < items.length; ++i) {
        const item = items[i];
        const itemType = getGroupingItemType(item);

        // Keep track of all lines by their ID.
        allLinesByID[item.ID] = item;

        // Create groups for all item types except item options.
        // Bundles will also be copied to child groups in resolveBundleLines.

        if (itemType === ItemType.ITEM_OPTION) {
            // Skip item option lines for now - handled by resolveItemOptionLines.
            continue;
        }

        // Construct an ID for the group for this line, based on the components that make each group/tab unique.
        let groupID = [
            `${itemType}s`,
            item.session?.ID || null,
            item.bookingGroup?.ID || null,
        ].join('-');

        // If this group does not already exist, create it.
        if (!itemGroups.hasOwnProperty(groupID)) {
            const session = item.getSession();

            const group = new OrderItemGroup({
                bookingGroup: item.bookingGroup,
                ID: groupID,
                itemType,
                session,
            });

            if (session) {
                group.isDifferentTimezone = session.isDifferentTimezone();
            }

            group.isValidOpenTicket = itemType === ItemType.TICKET && !session;

            itemGroups[groupID] = group;
        }

        itemGroups[groupID].items.push(item);
    }

    const groups = Object.values(itemGroups);

    resolveBundleLines(groups, allLinesByID);
    resolveItemOptionLines(groups, allLinesByID);

    sortItemGroups(groups);

    return groups;
};

/**
 * @param {OrderItemGroup[]} groups - The item groups created by item type/session/booking group.
 * @param {Object<number, OrderLine>} allLinesByID - All original order line items, organised by their ID.
 */
const resolveBundleLines = (groups, allLinesByID) => {
    // Iterate through each group, and each item in the group.
    // Insert any bundle lines immediately before the first of their children in that group.
    for (const group of groups) {
        let bundlesInsertedToGroup = new Set();

        for (const groupLine of group.items) {
            if (!groupLine.parentID) {
                // This line is not a child of anything, so skip it.
                continue;
            }

            const parentLine = allLinesByID[groupLine.parentID];
            if (!parentLine || parentLine.itemType !== ItemType.BUNDLE) {
                // Parent line does not exist or is not a bundle, so skip it.
                continue;
            }

            if (bundlesInsertedToGroup.has(parentLine.ID)) {
                // This bundle has already been inserted into this group, so skip it.
                continue;
            }

            // Clone the bundle line to avoid modifying the original.
            const bundleClone = cloneShallow(parentLine);
            bundleClone.isClone = true;

            const firstChildIndex = group.items.findIndex((line) => line.ID === groupLine.ID);
            if (firstChildIndex !== -1) {
                // Insert the bundle line immediately before the first child in this group.
                group.items.splice(firstChildIndex, 0, bundleClone);
                bundlesInsertedToGroup.add(parentLine.ID);
            }
        }
    }
};

/**
 * @param {OrderItemGroup[]} groups - The item groups created by item type/session/booking group.
 * @param {Object<number, OrderLine>} allLinesByID - All original order line items, organised by their ID.
 */
const resolveItemOptionLines = (groups, allLinesByID) => {
    // Arrange item option lines into a map by their parent ID.
    const itemOptionsByParentID = {};
    for (const line of Object.values(allLinesByID)) {
        if (line.itemType === ItemType.ITEM_OPTION) {
            if (!itemOptionsByParentID[line.parentID]) {
                itemOptionsByParentID[line.parentID] = [];
            }
            itemOptionsByParentID[line.parentID].push(line);
        }
    }

    // Iterate through each group, and each line in the group.
    // Insert any options for the line immediately after their parent item.
    for (const group of groups) {
        for (const groupLine of group.items) {
            if (!itemOptionsByParentID[groupLine.ID]) {
                // No options to insert.
                continue;
            }

            const optionsToInsert = itemOptionsByParentID[groupLine.ID];
            const parentIndex = group.items.findIndex((line) => line.ID === groupLine.ID);
            if (parentIndex !== -1) {
                // Insert all options immediately after the parent item.
                group.items.splice(parentIndex + 1, 0, ...optionsToInsert);
            }
        }
    }
};

/**
 * Sort the tabs/groups.
 * - Put tickets first, sorted by session start time, then by booking group name (those with names first).
 * - Then open tickets.
 * - Finally, other item types sorted alphabetically.
 *
 * @param {OrderItemGroup[]} groups
 */
const sortItemGroups = (groups) => {
    groups.sort((a, b) => {
        if (a.itemType === ItemType.TICKET && b.itemType === ItemType.TICKET) {
            // Both are tickets
            if (a.session && b.session) {
                // Both have sessions.

                // 1. Sort by session start time.
                const timeDiff = a.session.startTime.getTime() - b.session.startTime.getTime();
                if (timeDiff !== 0) {
                    return timeDiff;
                }

                // 2. Same session - sort by presence of booking group name first.
                const hasNameA = !!a.bookingGroup?.name;
                const hasNameB = !!b.bookingGroup?.name;

                if (hasNameA !== hasNameB) {
                    // Items with a group name come first.
                    return hasNameA ? -1 : 1;
                }

                // 3. If both have names (or both lack names), sort alphabetically by name.
                // (If both have no name this won't do anything)
                const nameA = a.bookingGroup?.name || '';
                const nameB = b.bookingGroup?.name || '';
                const nameDiff = nameA.localeCompare(nameB);
                if (nameDiff !== 0) {
                    return nameDiff;
                }
            }

            // One has a session, the other doesn't - put the one with the session first.
            if (a.session || b.session) {
                return a.session ? -1 : 1;
            }
        }

        // One is a ticket, the other isn't - put the ticket first.
        if (a.itemType === ItemType.TICKET || b.itemType === ItemType.TICKET) {
            return a.itemType === ItemType.TICKET ? -1 : 1;
        }

        // Neither is a ticket - sort by the same canonical tab order used on the Sell screen in the ItemPanelTabs
        // component so tabs always appear in the same order in both places.
        return compareItemTypesByTabOrder(a.itemType, b.itemType);
    });
};

/**
 * @typedef {Object} OrderItemTab
 * @property {string} itemType
 * @property {string} nameLangKey
 * @property {string} icon
 * @property {OrderItemGroup[]} groups
 */

/**
 * Group an array of OrderItemGroups into tabs, keyed by itemType.
 *
 * This groups itemGroups by itemType, so that all tickets whether event based or open
 * are grouped under a single "Tickets" tab and each group is rendered in its own section.
 *
 * Groups must already be in their final display order (i.e. this should be called with
 * the result of createItemGroups, after sortItemGroups has run). This function preserves
 * that order both between tabs and between groups within a tab, it does not re-sort.
 *
 * TODO: can / should we refactor so we don't have to make sequential method calls?
 *
 * @param {OrderItemGroup[]} groups - The item groups created by createItemGroups.
 *
 * @return {OrderItemTab[]}
 */
const createItemTabs = (groups) => {
    /**
     * @type {Object<string, OrderItemTab>}
     */
    const tabsByItemType = {};

    /**
     * @type {OrderItemTab[]}
     */
    const tabs = [];

    for (const group of groups) {
        if (!tabsByItemType.hasOwnProperty(group.itemType)) {
            const tab = {
                itemType: group.itemType,
                nameLangKey: getItemTypeTabLangKey(group.itemType),
                icon: getItemTypeTabIcon(group.itemType),
                groups: [],
            };

            tabsByItemType[group.itemType] = tab;
            tabs.push(tab);
        }

        tabsByItemType[group.itemType].groups.push(group);
    }

    return tabs;
};

module.exports = {
    createItemGroups,
    createItemTabs,
};
